/**
 * Estimation decks. Card values are stored as opaque strings so a deck can mix
 * numbers with non-numeric cards; `coffee` and `?` are the two special cards
 * every deck carries and neither takes part in numeric statistics.
 */

export const UNKNOWN_CARD = '?';
export const BREAK_CARD = 'coffee';

export const SPECIAL_CARDS: readonly string[] = [UNKNOWN_CARD, BREAK_CARD];

export const DECK_IDS = ['fibonacci', 'modified-fibonacci', 't-shirt', 'powers-of-two'] as const;

export type DeckId = (typeof DECK_IDS)[number];

/**
 * What a card is worth in calendar time, anchored at one point ≈ one day. The
 * short form is printed under the card face and is budgeted to fit the smallest
 * card the hand ever draws; the long form is for accessible names, where there
 * is no width to run out of.
 */
export interface CardMeaning {
  readonly short: string;
  readonly long: string;
}

export interface Deck {
  readonly id: DeckId;
  readonly label: string;
  readonly description: string;
  readonly cards: readonly string[];
  /** Meanings for this deck's own cards; the special cards are shared. */
  readonly meanings: Readonly<Record<string, CardMeaning>>;
}

/**
 * `?` and the break card mean the same thing whichever deck is on the table, so
 * they are defined once rather than copied into every deck.
 */
export const SPECIAL_CARD_MEANINGS: Readonly<Record<string, CardMeaning>> = {
  [UNKNOWN_CARD]: { short: 'unsure', long: 'Not enough information to estimate' },
  [BREAK_CARD]: { short: 'break', long: 'I need a break' },
};

/**
 * The largest card of the numeric decks reads `split` rather than a duration. A
 * 13 is not an estimate, it is a refusal to give one, and printing "three
 * weeks" there would invite the room to accept it as an answer. The t-shirt
 * deck is the exception: it stops at XL, which is a real estimate.
 */
const TOO_BIG: CardMeaning = { short: 'split', long: 'Too big to size — split it' };

export const DECKS: Readonly<Record<DeckId, Deck>> = {
  fibonacci: {
    id: 'fibonacci',
    label: 'Fibonacci',
    description: 'The classic sequence — 1, 2, 3, 5, 8 …',
    cards: ['0', '1', '2', '3', '5', '8', '13', UNKNOWN_CARD, BREAK_CARD],
    meanings: {
      '0': { short: 'none', long: 'No work at all' },
      '1': { short: '1 day', long: 'About a day' },
      '2': { short: '2 days', long: 'About two days' },
      '3': { short: '3 days', long: 'About three days' },
      '5': { short: '1 wk', long: 'About a week' },
      '8': { short: '2 wks', long: 'About two weeks' },
      '13': TOO_BIG,
    },
  },
  'modified-fibonacci': {
    id: 'modified-fibonacci',
    label: 'Modified Fibonacci',
    description: 'Rounder numbers at the top end, with a half point',
    cards: ['0', '0.5', '1', '2', '3', '5', '8', UNKNOWN_CARD, BREAK_CARD],
    meanings: {
      '0': { short: 'none', long: 'No work at all' },
      '0.5': { short: 'hours', long: 'A few hours' },
      '1': { short: '1 day', long: 'About a day' },
      '2': { short: '2 days', long: 'About two days' },
      '3': { short: '3 days', long: 'About three days' },
      '5': { short: '1 wk', long: 'About a week' },
      '8': { short: 'split', long: 'Two weeks or more — split it' },
    },
  },
  't-shirt': {
    id: 't-shirt',
    label: 'T-shirt sizes',
    description: 'Relative sizes, with a rough time for each.',
    // This deck tops out at XL rather than carrying a "too big" card: XL is a
    // real estimate here — more than a week — not a refusal to give one.
    cards: ['XS', 'S', 'M', 'L', 'XL', UNKNOWN_CARD, BREAK_CARD],
    meanings: {
      XS: { short: 'hours', long: 'A couple of hours of work' },
      S: { short: '≤1 day', long: 'A day or less' },
      M: { short: '2–3 d', long: 'Two to three days' },
      L: { short: '3d–1wk', long: 'Three days to a week of work' },
      XL: { short: '>1 wk', long: 'More than a week of work' },
    },
  },
  'powers-of-two': {
    id: 'powers-of-two',
    label: 'Powers of two',
    description: 'Doubling scale — 1, 2, 4, 8, 16 …',
    cards: ['0', '1', '2', '4', '8', '16', '32', UNKNOWN_CARD, BREAK_CARD],
    meanings: {
      '0': { short: 'none', long: 'No work at all' },
      '1': { short: '1 day', long: 'About a day' },
      '2': { short: '2 days', long: 'About two days' },
      '4': { short: '1 wk', long: 'About a week' },
      '8': { short: '2 wks', long: 'About two weeks' },
      '16': { short: '1 mo', long: 'About a month' },
      '32': TOO_BIG,
    },
  },
};

export const DEFAULT_DECK_ID: DeckId = 'fibonacci';

export function isDeckId(value: unknown): value is DeckId {
  return typeof value === 'string' && (DECK_IDS as readonly string[]).includes(value);
}

export function getDeck(deckId: DeckId): Deck {
  return DECKS[deckId];
}

export function isSpecialCard(value: string): boolean {
  return SPECIAL_CARDS.includes(value);
}

/**
 * The text to print for a card. Numeric and size cards print as-is; `?` is a real
 * character on a real card and stays one. The break card gets a word rather than
 * an emoji — clients draw it as an icon where there is room for one.
 */
export function cardLabel(value: string): string {
  return value === BREAK_CARD ? 'Break' : value;
}

/**
 * What a card is worth, or null for a value the deck does not hold — callers
 * render nothing rather than crashing a card face on an unknown value.
 */
export function cardMeaning(deck: Deck, value: string): CardMeaning | null {
  return SPECIAL_CARD_MEANINGS[value] ?? deck.meanings[value] ?? null;
}

/**
 * Accessible name for a card, for buttons and seats. The special cards speak
 * for themselves; a valued card carries its meaning so the number is never
 * announced without the thing it costs.
 */
export function cardName(value: string, meaning: CardMeaning | null): string {
  if (meaning === null) return `Estimate ${value}`;
  if (isSpecialCard(value)) return meaning.long;
  return `Estimate ${value}, ${meaning.long.charAt(0).toLowerCase()}${meaning.long.slice(1)}`;
}

/** Numeric weight of a card, or null for size and special cards. */
export function cardNumber(value: string): number | null {
  if (isSpecialCard(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
